import React, { lazy, Suspense, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Calendar,
  CheckCircle2,
  Download,
  FileText,
  FolderOpen,
  Globe,
  Image as ImageIcon,
  Keyboard,
  Layers,
  Package,
  Play,
  Settings,
  Shield,
  Sparkles,
  TrendingUp,
  Wand2,
  Clock,
  Zap
} from 'lucide-react';
import PremiumPublicHomeLanding from './homepage/PremiumPublicHomeLanding.jsx';
import SchemaOrg from './SEO/SchemaOrg';
import PageDiscoveryLinks from './SEO/PageDiscoveryLinks.jsx';
import { usePublicAppContext } from '../contexts/PublicAppContext';
import {
  TRIAL_FLOW_COPY_ID,
  UX_COPY_ID,
} from '../data/i18n/guestLanding.id.js';
import {
  TRIAL_FLOW_COPY_TH,
  UX_COPY_TH,
} from '../data/i18n/guestLanding.th.js';
import {
  TRIAL_FLOW_COPY_BN,
  UX_COPY_BN,
} from '../data/i18n/guestLanding.bn.js';
import {
  TRIAL_FLOW_COPY_HI,
  UX_COPY_HI,
} from '../data/i18n/guestLanding.hi.js';
import {
  TRIAL_FLOW_COPY_PT,
  UX_COPY_PT,
} from '../data/i18n/guestLanding.pt.js';
import {
  TRIAL_FLOW_COPY_IT as TRIAL_FLOW_COPY_IT_FALLBACK,
  UX_COPY_IT as UX_COPY_IT_FALLBACK
} from '../data/i18n/guestLanding.it.js';
import { getLanguageMarket, normalizeLanguage } from '../locales/index.js';
import { SEO_SUPPORTED_LANGUAGE_CODES, getSupportedCountryCodes, getSupportedLocaleTags } from '../utils/seoMarkets.js';
import {
  buildHrefLangEntries,
  buildLocalizedUrl,
  buildOgLocaleAlternates,
  localizeSeoHref
} from '../utils/localeRouting';
import {
  DEFAULT_SOCIAL_IMAGE,
  SEO_BASE_URL,
  ORGANIZATION_LOGO,
  ORGANIZATION_SAME_AS,
  ORGANIZATION_SUPPORT_EMAIL,
  buildItemListSchema,
  buildThingEntities,
  toKeywordString
} from '../utils/seoDiscovery.js';
import {
  ORGANIZATION_SCHEMA_ID,
  SOFTWARE_APPLICATION_SCHEMA_ID,
  WEBSITE_SCHEMA_ID,
  buildWebPageSchemaId
} from '../utils/structuredDataIdentity.js';
import {
  STOCKTAG_METADATA_STUDIO_VIDEO,
  buildVideoObjectSchema
} from '../data/productVideoCore.js';
import {
  containsRetiredPublicByokCopy,
  sanitizePublicCreditOnlyContent
} from '../utils/publicCreditOnlyContent.js';

const EmbeddedHomeLandingSections = lazy(() => import('./PublicHomeLandingSections.jsx'));

const LandingSectionsBoundary = ({ isPublic, children }) => (
  isPublic
    ? children
    : <Suspense fallback={<div className="guest-landing-loading" aria-hidden="true" />}>{children}</Suspense>
);

const HOME_TITLE = 'Stocktag | AI Stock Photo Keyword Generator Online';
const HOME_DESCRIPTION = 'Generate stock photo keywords, titles, and descriptions with AI. Export CSV/XMP for Adobe Stock, Shutterstock, Getty Images, and iStock in one workflow.';
const HOME_IMAGE = DEFAULT_SOCIAL_IMAGE.url;
// The home hreflang cluster. HomePage passes the same entries to the footer's
// language alternates nav, so the visible links follow the <head> exactly.
export const buildHomeHrefLangEntries = () => buildHrefLangEntries({
  baseUrl: SEO_BASE_URL,
  path: '/',
  languages: SEO_SUPPORTED_LANGUAGE_CODES
});
const SUPPORTED_SEO_LOCALE_TAGS = getSupportedLocaleTags();
const SUPPORTED_SEO_COUNTRIES = getSupportedCountryCodes();
const UX_COPY_BY_LANGUAGE = {
  en: {
    quickNavAria: 'Quick links to page sections',
    skipToSections: 'Skip to the section menu',
    jumpToStudioHint: 'Shortcut: Alt + S'
  },
  tr: {
    quickNavAria: 'Bölümlere hızlı erişim',
    skipToSections: 'Bölüm menüsüne geç',
    jumpToStudioHint: 'Kısayol: Alt + S'
  },
  es: {
    quickNavAria: 'Navegación rápida por secciones',
    skipToSections: 'Ir al menú de secciones',
    jumpToStudioHint: 'Atajo: Alt + S'
  },
  fr: {
    quickNavAria: 'Accès rapide aux différentes sections',
    skipToSections: 'Accéder au menu des sections',
    jumpToStudioHint: 'Raccourci : Alt + S'
  },
  de: {
    quickNavAria: 'Schnellnavigation durch die Bereiche',
    skipToSections: 'Zur Bereichsnavigation wechseln',
    jumpToStudioHint: 'Tastenkürzel: Alt + S'
  }
};
UX_COPY_BY_LANGUAGE.it = UX_COPY_IT_FALLBACK;
UX_COPY_BY_LANGUAGE.id = UX_COPY_ID;
UX_COPY_BY_LANGUAGE.th = UX_COPY_TH;
UX_COPY_BY_LANGUAGE.bn = UX_COPY_BN;
UX_COPY_BY_LANGUAGE.hi = UX_COPY_HI;
UX_COPY_BY_LANGUAGE.pt = UX_COPY_PT;
const TRIAL_FLOW_COPY_BY_LANGUAGE = {
  en: {
    flowLabel: 'How it works',
    flowSteps: [
      'Choose one image for a quick test run.',
      'Continue in Metadata Studio with your chosen marketplace.',
      'Review and edit the result before export.'
    ]
  },
  tr: {
    flowLabel: 'Nasıl çalışır?',
    flowSteps: [
      'Hızlı bir deneme için tek bir görsel seçin.',
      'Seçtiğiniz stok platformuyla Metadata Studio’ya geçin.',
      'Dışa aktarmadan önce sonucu inceleyip düzenleyin.'
    ]
  },
  es: {
    flowLabel: 'Qué pasa después',
    flowSteps: [
      'Elige una imagen para una prueba rápida.',
      'Se abre Metadata Studio con el marketplace que hayas elegido.',
      'Revisa y edita el resultado antes de exportar.'
    ]
  },
  fr: {
    flowLabel: 'Comment ça marche ?',
    flowSteps: [
      'Choisissez une image pour lancer une démo rapide.',
      'Metadata Studio s’ouvre avec la plateforme stock que vous avez choisie.',
      'Vérifiez et modifiez le résultat avant de l’exporter.'
    ]
  },
  de: {
    flowLabel: 'So geht es weiter',
    flowSteps: [
      'Wählen Sie ein Bild für einen schnellen Testlauf.',
      'Metadata Studio öffnet sich mit dem ausgewählten Zielmarktplatz.',
      'Prüfen und bearbeiten Sie das Ergebnis, bevor Sie es exportieren.'
    ]
  }
};
TRIAL_FLOW_COPY_BY_LANGUAGE.it = TRIAL_FLOW_COPY_IT_FALLBACK;
TRIAL_FLOW_COPY_BY_LANGUAGE.id = TRIAL_FLOW_COPY_ID;
TRIAL_FLOW_COPY_BY_LANGUAGE.th = TRIAL_FLOW_COPY_TH;
TRIAL_FLOW_COPY_BY_LANGUAGE.bn = TRIAL_FLOW_COPY_BN;
TRIAL_FLOW_COPY_BY_LANGUAGE.hi = TRIAL_FLOW_COPY_HI;
TRIAL_FLOW_COPY_BY_LANGUAGE.pt = TRIAL_FLOW_COPY_PT;
const HOME_BAND_LABELS = {
  en: {
    hero: 'Overview',
    workflow: 'Workflow',
    tools: 'Tools',
    outputs: 'Outputs',
    close: 'Plans + FAQ'
  },
  tr: {
    hero: 'Genel Bakış',
    workflow: 'Akış',
    tools: 'Araçlar',
    outputs: 'Çıktılar',
    close: 'Planlar + SSS'
  },
  es: {
    hero: 'Resumen',
    workflow: 'Flujo',
    tools: 'Herramientas',
    outputs: 'Resultados',
    close: 'Planes y preguntas'
  },
  fr: {
    hero: 'Vue d’ensemble',
    workflow: 'Fonctionnement',
    tools: 'Outils',
    outputs: 'Résultats',
    close: 'Offres + FAQ'
  },
  de: {
    hero: 'Überblick',
    workflow: 'Workflow',
    tools: 'Werkzeuge',
    outputs: 'Ergebnisse',
    close: 'Tarife & FAQ'
  },
  it: {
    hero: 'Panoramica',
    workflow: 'Workflow',
    tools: 'Strumenti',
    outputs: 'Output',
    close: 'Piani + FAQ'
  },
  id: {
    hero: 'Ringkasan',
    workflow: 'Alur kerja',
    tools: 'Alat',
    outputs: 'Hasil',
    close: 'Paket + pertanyaan umum'
  },
  th: {
    hero: 'ภาพรวม',
    workflow: 'ขั้นตอนการทำงาน',
    tools: 'เครื่องมือ',
    outputs: 'ผลลัพธ์',
    close: 'แพ็กเกจ + FAQ'
  },
  bn: {
    hero: 'সংক্ষিপ্ত বিবরণ',
    workflow: 'ওয়ার্কফ্লো',
    tools: 'টুল',
    outputs: 'আউটপুট',
    close: 'প্ল্যান + সাধারণ প্রশ্ন'
  },
  hi: {
    hero: 'अवलोकन',
    workflow: 'वर्कफ़्लो',
    tools: 'टूल',
    outputs: 'आउटपुट',
    close: 'प्लान + सामान्य प्रश्न'
  },
  pt: {
    hero: 'Visão geral',
    workflow: 'Fluxo de trabalho',
    tools: 'Ferramentas',
    outputs: 'Resultados',
    close: 'Planos + FAQ'
  }
};
const getScrollBehavior = () => {
  if (typeof window === 'undefined') return 'auto';
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth';
};

const stats = [
  { value: '15 + Custom', label: 'Platform CSV formats' },
  { value: '10', label: 'Metadata translation targets' },
  { value: 'Batch-first', label: 'Queue, review, and export' }
];

const CREDIT_ONLY_SALES_COPY = {
  en: {
    stripBodyGuest: 'Use the live demo and free credits to check the output, then choose a monthly credit plan or add one-time credits when your workload grows.',
    stripBodyFree: 'Continue with Stocktag-managed AI by adding one-time credits or choosing a monthly credit plan.',
    bullets: [
      'Use one Stocktag credit balance across supported workflows',
      'Choose a monthly credit plan for recurring production',
      'Add non-expiring one-time credits whenever you need extra capacity'
    ],
    cards: [
      { eyebrow: 'Free start', title: 'Try the output first', value: '10 free credits', note: 'New accounts can evaluate the workflow before purchasing.' },
      { eyebrow: 'Recurring work', title: 'Monthly credit plans', value: 'Built-in AI + monthly credits', note: 'Choose the monthly capacity that matches your production volume.' },
      { eyebrow: 'Flexible capacity', title: 'One-time credits', value: 'Non-expiring credit top-ups', note: 'Add credits without changing your active plan.' }
    ]
  },
  tr: {
    stripBodyGuest: 'Canlı demo ve ücretsiz kredilerle çıktıyı değerlendirin; iş hacminiz arttığında aylık kredi planı seçin veya tek seferlik kredi ekleyin.',
    stripBodyFree: 'Tek seferlik kredi ekleyerek veya aylık kredi planı seçerek Stocktag tarafından yönetilen yapay zekâyla devam edin.',
    bullets: [
      'Desteklenen iş akışlarında tek Stocktag kredi bakiyesi kullanın',
      'Düzenli üretim için aylık kredi planı seçin',
      'Ek kapasite gerektiğinde süresiz tek seferlik kredi ekleyin'
    ],
    cards: [
      { eyebrow: 'Ücretsiz başlangıç', title: 'Önce çıktıyı deneyin', value: '10 ücretsiz kredi', note: 'Yeni hesaplar satın alma yapmadan önce iş akışını değerlendirebilir.' },
      { eyebrow: 'Düzenli üretim', title: 'Aylık kredi planları', value: 'Yerleşik yapay zekâ + aylık kredi', note: 'Üretim hacminize uygun aylık kapasiteyi seçin.' },
      { eyebrow: 'Esnek kapasite', title: 'Tek seferlik krediler', value: 'Süresi dolmayan ek krediler', note: 'Aktif planınızı değiştirmeden kredi ekleyin.' }
    ]
  }
};

const buildCreditOnlySalesCopy = (copy, language) => sanitizePublicCreditOnlyContent({
  ...copy,
  ...(CREDIT_ONLY_SALES_COPY[language] || CREDIT_ONLY_SALES_COPY.en)
}, language);

const toolIntentLinks = [
  {
    href: '/stock-photo-keyword-generator',
    title: 'Stock Photo Keyword Generator',
    description: 'Turn an image into AI stock keywords with relevance ordering and export-ready output.',
    pill: 'Primary'
  },
  {
    href: '/stock-photo-keyword-generator-free',
    title: 'Free Stock Photo Keyword Generator',
    description: 'Use the free AI image keyword generator demo to preview keyword quality before signup.',
    pill: 'Free'
  },
  {
    href: '/stock-photo-metadata-generator',
    title: 'Stock Photo Metadata Generator',
    description: 'Create titles, descriptions, and keywords in one stock photo title-and-keyword workflow.',
    pill: 'Metadata'
  },
  {
    href: '/stock-photo-tag-generator',
    title: 'Stock Photo Tag Generator',
    description: 'Turn image content into searchable stock tags and use-case clusters before export.',
    pill: 'Tags'
  },
  {
    href: '/stock-photo-xmp-generator',
    title: 'Stock Photo XMP Generator',
    description: 'Embed reviewed IPTC/XMP metadata into JPEG and EPS files before upload.',
    pill: 'XMP'
  },
  {
    href: '/stock-photo-csv-generator',
    title: 'Stock Photo CSV Generator',
    description: 'Export platform-specific CSV metadata with presets for stock upload workflows.',
    pill: 'CSV'
  },
  {
    href: '/microstock-keyword-generator',
    title: 'Microstock Keyword Generator',
    description: 'Use a microstock keyword tool built for Adobe Stock, Shutterstock, Getty, and iStock contributors.',
    pill: 'Microstock'
  },
  {
    href: '/getty-keywords-generator',
    title: 'Getty Keywords Generator',
    description: 'Run a Getty Images and iStock keyword tool with controlled-vocabulary review.',
    pill: 'Getty'
  },
  {
    href: '/stock-photo-title-generator',
    title: 'Stock Photo Title Generator',
    description: 'Write searchable stock titles and connect them to the full title-and-keyword workflow.',
    pill: 'Titles'
  },
  {
    href: '/stock-photo-description-generator',
    title: 'Stock Photo Description Generator',
    description: 'Generate clean stock descriptions that stay useful and commercially safe.',
    pill: 'Descriptions'
  },
  {
    href: '/shutterstock-keywording-tool',
    title: 'Shutterstock Keywording Tool (7–50)',
    description: 'Use the Shutterstock keyword tool with 7-50 checks, relevance ordering, and free preview.',
    pill: 'Shutterstock'
  },
  {
    href: '/adobe-stock-keyword-order',
    title: 'Adobe Stock Keyword Order (Top 10)',
    description: 'Review the top 10 Adobe Stock priority terms with a dedicated keyword order tool.',
    pill: 'Adobe Stock'
  }
];

const comparisonIntentLinks = [
  {
    href: '/alternatives',
    title: 'Verified Alternatives Hub',
    description: 'Start with the comparison hub, then route each competitor query into the right Stocktag money page.',
    pill: 'Hub'
  },
  {
    href: '/vs/photokeyworder',
    title: 'Stocktag vs PhotoKeyworder.ai',
    description: 'Compare a keyword-only workflow against a full metadata pipeline.',
    pill: 'Keyword'
  },
  {
    href: '/vs/phototag',
    title: 'Stocktag vs PhotoTag.ai',
    description: 'See the tradeoffs in batch workflow, pricing, and review speed.',
    pill: 'Tagging'
  },
  {
    href: '/vs/pixify',
    title: 'Stocktag vs Pixify Studio',
    description: 'Compare Getty/iStock workflow coverage, FTP patterns, and metadata depth.',
    pill: 'Getty'
  },
  {
    href: '/vs/microstockplus',
    title: 'Stocktag vs Microstock+',
    description: 'Legacy contributor tooling versus a modern AI metadata workflow.',
    pill: 'Microstock'
  },
  {
    href: '/vs/deepmeta',
    title: 'Stocktag vs Deep.Meta',
    description: 'Adobe-first desktop workflow versus multi-platform AI metadata operations.',
    pill: 'Comparison'
  }
];

const HOME_COMPARISON_TITLES = {
  tr: {
    photokeyworder: 'Stocktag ve PhotoKeyworder.ai karşılaştırması',
    phototag: 'Stocktag ve PhotoTag.ai karşılaştırması',
    pixify: 'Stocktag ve Pixify Studio karşılaştırması',
    microstockplus: 'Stocktag ve Microstock+ karşılaştırması',
    deepmeta: 'Stocktag ve Deep.Meta karşılaştırması'
  },
  es: {
    photokeyworder: 'Stocktag y PhotoKeyworder.ai',
    phototag: 'Stocktag y PhotoTag.ai',
    pixify: 'Stocktag y Pixify Studio',
    microstockplus: 'Stocktag y Microstock+',
    deepmeta: 'Stocktag y Deep.Meta'
  },
  fr: {
    photokeyworder: 'Stocktag ou PhotoKeyworder.ai',
    phototag: 'Stocktag ou PhotoTag.ai',
    pixify: 'Stocktag ou Pixify Studio',
    microstockplus: 'Stocktag ou Microstock+',
    deepmeta: 'Stocktag ou Deep.Meta'
  },
  de: {
    photokeyworder: 'Stocktag und PhotoKeyworder.ai im Vergleich',
    phototag: 'Stocktag und PhotoTag.ai im Vergleich',
    pixify: 'Stocktag und Pixify Studio im Vergleich',
    microstockplus: 'Stocktag und Microstock+ im Vergleich',
    deepmeta: 'Stocktag und Deep.Meta im Vergleich'
  },
  it: {
    photokeyworder: 'Stocktag contro PhotoKeyworder.ai',
    phototag: 'Stocktag contro PhotoTag.ai',
    pixify: 'Stocktag rispetto a Pixify Studio',
    microstockplus: 'Stocktag contro Microstock+',
    deepmeta: 'Stocktag contro Deep.Meta'
  },
  id: {
    photokeyworder: 'Stocktag vs PhotoKeyworder.ai',
    phototag: 'Stocktag vs PhotoTag.ai',
    pixify: 'Stocktag vs Pixify Studio',
    microstockplus: 'Stocktag vs Microstock+',
    deepmeta: 'Stocktag vs Deep.Meta'
  },
  th: {
    photokeyworder: 'Stocktag กับ PhotoKeyworder.ai',
    phototag: 'Stocktag กับ PhotoTag.ai',
    pixify: 'Stocktag กับ Pixify Studio',
    microstockplus: 'Stocktag กับ ไมโครสต็อก+',
    deepmeta: 'Stocktag กับ Deep.Meta'
  }
};

const coreRouteLinks = [
  { href: '/stock-photo-keyword-generator', label: 'Stock Photo Keyword Generator' },
  { href: '/stock-photo-keyword-generator-free', label: 'Free Stock Photo Keyword Generator' },
  { href: '/stock-photo-metadata-generator', label: 'Stock Photo Metadata Generator' },
  { href: '/stock-photo-tag-generator', label: 'Stock Photo Tag Generator' },
  { href: '/stock-photo-xmp-generator', label: 'Stock Photo XMP Generator' },
  { href: '/stock-photo-csv-generator', label: 'Stock Photo CSV Generator' },
  { href: '/microstock-keyword-generator', label: 'Microstock Keyword Generator' },
  { href: '/getty-keywords-generator', label: 'Getty Keywords Generator' },
  { href: '/stock-photo-title-generator', label: 'Stock Photo Title Generator' },
  { href: '/stock-photo-description-generator', label: 'Stock Photo Description Generator' },
  { href: '/shutterstock-keywording-tool', label: 'Shutterstock Keywording Tool' },
  { href: '/adobe-stock-keyword-order', label: 'Adobe Stock Keyword Order' },
  { href: '/help/iptc-keywords-for-stock-photos', label: 'IPTC Keywords FAQ' },
  { href: '/alternatives', label: 'Verified Alternatives Hub' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/blog', label: 'Blog' },
  { href: '/metadata', label: 'Metadata Studio' }
];




const features = [
  {
    icon: Sparkles,
    title: 'Dual AI Engine',
    description: 'Hybrid vision + language models tuned for stock-ready metadata.'
  },
  {
    icon: Layers,
    title: 'Batch Processing',
    description: 'Manage large groups of files in a batch-native queue with progress tracking.'
  },
  {
    icon: Wand2,
    title: 'Precision Keywords',
    description: 'Generate clean titles, descriptions, and keyword sets fast.'
  },
  {
    icon: Download,
    title: 'Instant Exports',
    description: 'CSV downloads and metadata embedding in one workflow.'
  },
  {
    icon: Keyboard,
    title: 'Studio Shortcuts',
    description: 'Keyboard-first controls designed for speed and focus.'
  },
  {
    icon: Shield,
    title: 'Safe By Design',
    description: 'Controlled preview flows with clean, reversible edits.'
  }
];

const studioModules = [
  {
    icon: Settings,
    title: 'Metadata Controls',
    description: 'Tune title/description length, keyword rules, and platform presets.'
  },
  {
    icon: Sparkles,
    title: 'Art Style Detection',
    description: 'Surface style tags with optional deep analysis mode.'
  },
  {
    icon: ImageIcon,
    title: 'PNG + Vector Modes',
    description: 'Switch modes for transparent assets and illustration workflows.'
  },
  {
    icon: Clock,
    title: 'History & Drafts',
    description: 'Keep session history, restore drafts, and stay consistent.'
  },
  {
    icon: Download,
    title: 'CSV + Embedded Export',
    description: 'Export CSVs or embed metadata into files before upload.'
  },
];

const studioProof = [
  { icon: FileText, label: 'Metadata-ready titles + descriptions' },
  { icon: Package, label: 'Batch pipelines with progress tracking' },
  { icon: Zap, label: 'Fast review and regeneration flow' }
];

const metadataCapabilityCardIcons = [
  FolderOpen,
  FileText,
  Sparkles,
  Keyboard,
  Shield,
  Download
];

const metadataFlowIcons = [
  FolderOpen,
  Settings,
  CheckCircle2,
  Download
];

const platformList = [
  'Adobe Stock',
  'Shutterstock',
  'Canva',
  'Vecteezy',
  'Creative Fabrica',
  'Design Bundles',
  'Alamy',
  'Depositphotos',
  'Dreamstime',
  'Freepik',
  'Getty Images',
  'iStock',
  'Pond5',
  'Motion Elements',
  '123RF'
];

const comparison = {
  old: [
    'Manual keyword lists that take hours to write.',
    'Different CSV formats for each platform.',
    'No consistent naming across batches.'
  ],
  studio: [
    'AI-generated metadata with review control.',
    'Platform-specific exports ready to upload.',
    'Session history keeps your style consistent.'
  ]
};

const platformPresets = [
  {
    name: 'Adobe Stock',
    description: 'Preset rules tuned for agency-friendly metadata formatting.',
    bullets: ['Title + description length tuning', 'Keyword ordering and caps', 'Category mapping support'],
    sample: {
      title: 'Modern business team in collaborative office',
      tags: ['business', 'teamwork', 'office']
    }
  },
  {
    name: 'Shutterstock',
    description: 'Compliance checks that reduce rejections and clean up repeats.',
    bullets: ['Spam-safe keyword clusters', 'Clear category mapping', 'Export-ready CSV'],
    sample: {
      title: 'Confident professional working on laptop',
      tags: ['professional', 'technology', 'workspace']
    }
  },
  {
    name: 'Creative Fabrica',
    description: 'SEO-forward formatting for craft and template marketplaces.',
    bullets: ['Long-tail keyword focus', 'Style-aware title casing', 'Bundle-ready export'],
    sample: {
      title: 'Watercolor floral clipart bundle',
      tags: ['watercolor', 'floral', 'clipart']
    }
  },
  {
    name: 'Design Bundles',
    description: 'Structured tag sets that match marketplace expectations.',
    bullets: ['Consistent tag length', 'Format-aligned metadata', 'Batch download support'],
    sample: {
      title: 'Minimal logo mark set',
      tags: ['logo', 'minimal', 'branding']
    }
  }
];

const visualIntelligence = [
  {
    icon: Sparkles,
    title: 'Keyword Intelligence',
    description: 'Score marketability, title coverage, keyword strength, and search demand; surface opportunities and move the strongest terms forward.'
  },
  {
    icon: ImageIcon,
    title: 'PNG + Vector Modes',
    description: 'Switch modes for transparent assets and illustration workflows.'
  },
  {
    icon: Play,
    title: 'Video Metadata Queue',
    description: 'Handle video metadata with frame-aware processing support.'
  },
  {
    icon: Wand2,
    title: 'AI Edit Assistant',
    description: 'Describe a change in plain language and apply it to the selected Default or Getty/iStock draft, with undo.'
  },
  {
    icon: Download,
    title: '15 marketplace formats + Custom CSV',
    description: 'Choose marketplace presets, edit per-site column rules, build a reusable Custom CSV template, and deliver CSV, XMP, embedded metadata, or ZIP.'
  },
  {
    icon: Shield,
    title: 'Commercial rights review',
    description: 'Choose commercial or editorial mode. Stocktag checks metadata automatically, can run an optional two-pass visual identity review, and rechecks before export; signals are not legal advice.'
  }
];

const marketIntelligenceHighlights = [
  {
    icon: Calendar,
    title: 'Holiday Planner',
    description: 'Filter current public holidays and curated campaigns by country, timeframe, category, and asset type; copy keyword packs, briefs, and upload windows.'
  },
  {
    icon: TrendingUp,
    title: 'Trends Radar',
    description: 'Inspect momentum, search velocity, saturation, opportunities, and an action-ready metadata pack for a topic, using current signals when available.'
  },
  {
    icon: Globe,
    title: 'Similarity Scanner',
    description: 'Paste an Adobe Stock or Shutterstock URL, crawl the chosen scope, and visually rerank similar assets with confidence signals.'
  },
  {
    icon: Clock,
    title: 'Publish lead-time guidance',
    description: 'Use suggested upload windows to schedule metadata ahead of expected demand.'
  }
];

const imageGeneratorHighlights = [
  {
    icon: Sparkles,
    title: 'Reference-based creation and editing',
    description: 'Upload PNG, JPEG, or WebP references and use the controls supported by the selected model.'
  },
  {
    icon: Settings,
    title: 'Capability-aware settings',
    description: 'Adjust size, aspect ratio, quality, variants, format, background, compression, moderation, thinking, or grounding only when the model supports them.'
  },
  {
    icon: ImageIcon,
    title: 'Results workspace',
    description: 'Preview outputs, reuse settings, copy prompts, use a result as a new reference, or download one file or a ZIP.'
  },
  {
    icon: Download,
    title: 'Metadata-ready next step',
    description: 'Download the selected output and continue in Metadata Studio for titles, descriptions, keywords, review, and export.'
  }
];

const testimonials = [
  {
    quote: 'Batch-upload assets, review the generated metadata, and export the selected formats in one workflow.',
    author: 'Example 01',
    role: 'Illustrative scenario — not a customer testimonial'
  },
  {
    quote: 'Apply marketplace presets, refine titles and keywords, then download upload-ready exports.',
    author: 'Example 02',
    role: 'Illustrative scenario — not a customer testimonial'
  },
  {
    quote: 'Preview one supported image in the guest trial, then sign in for account-based workflows.',
    author: 'Example 03',
    role: 'Illustrative scenario — not a customer testimonial'
  }
];

const faqs = [
  {
    question: 'What is Stocktag Metadata Studio?',
    answer:
      'Stocktag is an AI metadata generator and stock keyword generator for stock photos, vectors, illustrations, and video. It creates SEO-ready titles, descriptions, and keywords and acts as stock metadata software to standardize naming across your library.'
  },
  {
    question: 'What is included in Market Intelligence?',
    answer:
      'Holiday Planner covers public holidays and curated campaigns; Trends Radar analyzes topic momentum and opportunities; Similarity Scanner works with Adobe Stock and Shutterstock URLs to rerank similar assets.'
  },
  {
    question: 'What does Vectorizer do?',
    answer:
      'Vectorizer converts raster image batches to SVG with VTracer. Every signed-in account gets 10 free image conversions per day; each additional image uses 1 Stocktag credit.'
  },
  {
    question: 'Which marketplace exports are supported?',
    answer:
      'Stocktag provides 15 marketplace CSV formats—Creative Fabrica, Design Bundles, Adobe Stock, Shutterstock, Canva, Vecteezy, Alamy, Depositphotos, Dreamstime, Freepik, Getty Images, iStock, Pond5, Motion Elements, and 123RF—plus Custom CSV, XMP, embedded metadata, and ZIP delivery.'
  },
  {
    question: 'Does the rights review guarantee commercial clearance?',
    answer:
      'No. It surfaces metadata and visual identity signals for review and supports commercial or editorial workflows, but it is not legal advice or a clearance guarantee.'
  },
  {
    question: 'Can I edit metadata before export?',
    answer:
      'Yes. Review titles, descriptions, and keywords in the list and detail panels, apply bulk edits, and regenerate before downloading.'
  },
  {
    question: 'Does Stocktag support multilingual metadata?',
    answer:
      'Yes. The studio includes translation previews and localization-ready keywording so you can target international search demand and global stock SEO.'
  },
  {
    question: 'How does the guest demo work?',
    answer:
      "Choose a supported image and target marketplace to preview a metadata result. The file is processed through Stocktag's guest metadata endpoint; sign in to continue with account-based workflows and saved sessions."
  },
  {
    question: 'Are files selected for the guest trial uploaded?',
    answer:
      "Yes. The selected image passes through a temporary browser handoff and is sent to Stocktag's guest metadata endpoint to generate the result. Processing and retention are governed by the Privacy Notice."
  },
  {
    question: 'Why are guest previews limited?',
    answer:
      'Guest limits help protect the service from abuse and keep the demo responsive. Sign in to use the account-based processing, export, and history options available with your plan.'
  },
  {
    question: 'Is batch processing supported?',
    answer:
      'Yes. Stocktag is built for batch uploads, progress tracking, and session history so you can handle large libraries quickly and keep metadata consistent.'
  },
];

const keywordTranslations = {
  businesswoman: [
    'mujer de negocios',
    'femme d\'affaires',
    'geschäftsfrau',
    'donna d\'affari',
    'delovaya zhenshchina',
    'ビジネスウーマン',
    '商务女性',
    'mulher de negócios',
    'zakenvrouw'
  ],
  blue: ['azul', 'bleu', 'blau', 'blu', 'siniy', 'ブルー', '蓝色', 'azul', 'blauw'],
  blazer: ['blazer', 'blazer', 'blazer', 'blazer', 'блейзер', 'ブレザー', '西装外套', 'blazer', 'blazer'],
  confident: [
    'confiada',
    'confiante',
    'selbstbewusst',
    'fiduciosa',
    'uverennaya',
    '自信のある',
    '自信的',
    'confiante',
    'zelfverzekerd'
  ],
  office: ['oficina', 'bureau', 'büro', 'ufficio', 'офис', 'オフィス', '办公室', 'escritório', 'kantoor'],
  executive: [
    'ejecutiva',
    'cadre dirigeante',
    'führungskraft',
    'dirigente',
    'rukovoditel',
    '管理職',
    '高管',
    'executiva',
    'leidinggevende'
  ],
  leadership: [
    'liderazgo',
    'leadership',
    'führung',
    'leadership',
    'liderstvo',
    'リーダーシップ',
    '领导力',
    'liderança',
    'leiderschap'
  ],
  corporate: [
    'corporativo',
    'entreprise',
    'unternehmens',
    'aziendale',
    'korporativnyy',
    '企業の',
    '企业',
    'corporativo',
    'zakelijk'
  ],
  coffee: ['café', 'café', 'kaffee', 'caffè', 'kofe', 'コーヒー', '咖啡', 'café', 'koffie'],
  cafe: ['cafetería', 'café', 'cafe', 'caffetteria', 'kafe', 'カフェ', '咖啡馆', 'cafeteria', 'café'],
  cup: ['taza', 'tasse', 'tasse', 'tazza', 'chashka', 'カップ', '杯子', 'xícara', 'beker'],
  logo: ['logo', 'logo', 'logo', 'logo', 'logotip', 'ロゴ', '标志', 'logotipo', 'logo'],
  branding: [
    'marca',
    'image de marque',
    'markenbildung',
    'branding',
    'brending',
    'ブランディング',
    '品牌塑造',
    'branding',
    'branding'
  ],
  vector: ['vector', 'vecteur', 'vektor', 'vettoriale', 'vektor', 'ベクター', '矢量', 'vetorial', 'vector'],
  steaming: [
    'humeante',
    'fumant',
    'dampfend',
    'fumante',
    'parashchiy',
    '湯気の出る',
    '冒蒸汽的',
    'fumegante',
    'dampend'
  ],
  circular: [
    'circular',
    'circulaire',
    'kreisförmig',
    'circolare',
    'krugliy',
    '円形の',
    '圆形的',
    'circular',
    'cirkelvormig'
  ],
  aerial: ['aéreo', 'aérien', 'luftaufnahme', 'aereo', 'vozdushnyy', '空中の', '航拍', 'aéreo', 'lucht'],
  drone: ['dron', 'drone', 'drohne', 'drone', 'dron', 'ドローン', '无人机', 'drone', 'drone'],
  coastline: [
    'costa',
    'littoral',
    'küstenlinie',
    'linea costiera',
    'beregovaya liniya',
    '海岸線',
    '海岸线',
    'litoral',
    'kustlijn'
  ],
  ocean: ['océano', 'océan', 'ozean', 'oceano', 'okean', '海洋', '海洋', 'oceano', 'oceaan'],
  waves: ['olas', 'vagues', 'wellen', 'onde', 'volny', '波', '波浪', 'ondas', 'golven'],
  travel: ['viaje', 'voyage', 'reise', 'viaggio', 'puteshestvie', '旅行', '旅行', 'viagem', 'reizen'],
  sunset: [
    'atardecer',
    'coucher de soleil',
    'sonnenuntergang',
    'tramonto',
    'zakat',
    '夕日',
    '日落',
    'pôr do sol',
    'zonsondergang'
  ]
};

const languages = ['ES', 'FR', 'DE', 'IT', 'RU', 'JA', 'ZH', 'PT', 'NL'];

const metadataSamples = [
  {
    mediaType: 'image',
    mediaSrc: '/PORTRAIT-640.webp',
    mediaWidth: 640,
    mediaHeight: 349,
    mediaAlt: 'Portrait sample of a female executive in an office',
    mediaLabel: 'Photo',
    title: 'Female executive portrait in modern corporate office',
    description:
      'Confident professional in a glass-walled workspace, ideal for leadership, career growth, and corporate success themes.',
    keywords: ['businesswoman', 'executive', 'leadership', 'corporate', 'office']
  },
  {
    mediaType: 'image',
    mediaSrc: '/coffelogo-640.webp',
    mediaWidth: 640,
    mediaHeight: 358,
    mediaAlt: 'Coffee logo sample for vector metadata',
    mediaLabel: 'Vector',
    title: 'Coffee shop badge logo vector with steaming cup icon',
    description: 'Clean vector badge for cafe branding, takeaway packaging, menu design, and social media identity.',
    keywords: ['coffee', 'logo', 'cafe', 'vector', 'branding']
  },
  {
    mediaType: 'video',
    mediaSrc: '/VIDEO.mp4',
    mediaPoster: '/VIDEO-poster.webp',
    mediaAlt: 'Video sample of tropical coastline',
    mediaLabel: 'Video',
    title: 'Aerial drone footage of tropical ocean coastline at sunset',
    description:
      'Cinematic flyover with ocean waves and warm evening light, perfect for travel, tourism, and nature storytelling.',
    keywords: ['drone', 'aerial', 'coastline', 'ocean', 'sunset']
  }
];

const safeguardCards = [
  {
    title: 'Transparent guest processing',
    description: 'Your selected image is sent to the guest metadata endpoint; processing and retention follow the Privacy Notice.'
  },
  {
    title: 'Export Control',
    description: 'Choose which platform formats to include for every session.'
  },
  {
    title: 'Edit Before Export',
    description: 'Refine titles, descriptions, and tags before download.'
  },
  {
    title: 'History + Drafts',
    description: 'Keep your session consistent with built-in history and draft recovery.'
  }
];

const steps = [
  {
    title: 'Drop Assets',
    description: 'Drag images or videos into the studio to start.'
  },
  {
    title: 'Review & Refine',
    description: 'Edit titles, descriptions, and keywords with quick actions.'
  },
  {
    title: 'Export & Publish',
    description: 'Download CSVs or embed metadata before uploading.'
  }
];

const mergeLocalizedValue = (baseValue, localizedValue) => {
  if (localizedValue === undefined) return baseValue;
  if (Array.isArray(baseValue) && Array.isArray(localizedValue)) return localizedValue;
  if (
    baseValue &&
    localizedValue &&
    typeof baseValue === 'object' &&
    typeof localizedValue === 'object' &&
    !Array.isArray(baseValue) &&
    !Array.isArray(localizedValue)
  ) {
    const merged = { ...baseValue };
    Object.keys(localizedValue).forEach((key) => {
      merged[key] = mergeLocalizedValue(baseValue[key], localizedValue[key]);
    });
    return merged;
  }
  return localizedValue;
};

const mergeLocalizedItems = (baseItems, localizedItems) => {
  if (!Array.isArray(baseItems)) return [];
  if (!Array.isArray(localizedItems) || localizedItems.length === 0) return baseItems;
  return baseItems.map((item, index) => mergeLocalizedValue(item, localizedItems[index]));
};

const GuestLandingContent = ({
  variant = 'embedded',
  theme = 'light',
  themeReady = false,
  onScrollToStudio,
  onStartDemo,
  onOpenTour,
  onOpenLogin,
  onOpenPricing,
  studioHref = '',
  demoHref = '',
  tourHref = '',
  loginHref = '',
  pricingHref = '',
  demoStarted,
  trialImageAlt,
  trialPlatforms = [],
  onRunTrial,
  trialLoading = false,
  trialError = '',
  authLoading = false,
  isAuthenticated = false,
  isFreeMember = false,
  isUnlimitedPlan = false,
  blogPosts = [],
  blogPreviewRef = null,
  onOpenBlog,
  onOpenBlogIndex,
  isGuest = true,
  publicHomePayload
}) => {
  const appContext = usePublicAppContext();
  const language = appContext?.language || 'en';
  const homeContent = publicHomePayload.homeContent;
  const metadataCapabilityContent = publicHomePayload.metadataCapabilityContent;
  const homeVideoCopy = publicHomePayload.homeVideoCopy;
  const metadataStoryCopy = publicHomePayload.metadataStoryCopy;
  const publicHomeUiCopy = publicHomePayload.publicHomeUiCopy;
  const homeShowcaseCopy = publicHomePayload.homeShowcaseCopy;
  const showAuthenticatedUi = !authLoading && isAuthenticated;
  const showGuestUi = !isAuthenticated;
  const showPricingPrompt = !isUnlimitedPlan && (showGuestUi || isFreeMember);
  const uiCopy = useMemo(
    () => UX_COPY_BY_LANGUAGE[language] || UX_COPY_BY_LANGUAGE.en,
    [language]
  );
  const trialFlowCopy = useMemo(
    () => TRIAL_FLOW_COPY_BY_LANGUAGE[language] || TRIAL_FLOW_COPY_BY_LANGUAGE.en,
    [language]
  );
  const salesCopy = useMemo(
    () => buildCreditOnlySalesCopy({}, language),
    [language]
  );
  const localeMarket = getLanguageMarket(language);
  const homeTitle = homeContent.seoTitle || HOME_TITLE;
  const homeDescription = homeContent.seoDescription || HOME_DESCRIPTION;
  const ogLocale = localeMarket.ogLocale;
  const canonicalUrl = buildLocalizedUrl(SEO_BASE_URL, '/', language);
  const hreflangLinks = useMemo(() => buildHomeHrefLangEntries(), []);
  const ogLocaleAlternates = useMemo(() => buildOgLocaleAlternates({
    languages: SEO_SUPPORTED_LANGUAGE_CODES,
    currentLanguage: language || 'en'
  }), [language]);

  const [isMounted, setIsMounted] = useState(false);
  const [galleryData, setGalleryData] = useState([]);
  const [galleryMeta, setGalleryMeta] = useState(null);
  const galleryMetaLoadingRef = useRef(false);

  useEffect(() => {
    setIsMounted(true);

    if (variant !== 'embedded') return undefined;

    const loadGallery = async () => {
      try {
        const module = await import('../data/heroGallery.js');
        setGalleryData(module.default);
      } catch (error) {
        console.error('Failed to load gallery', error);
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => loadGallery(), { timeout: 1500 });
    } else {
      setTimeout(loadGallery, 0);
    }
    return undefined;
  }, [variant]);

  const ensureGalleryMeta = async () => {
    if (galleryMeta || galleryMetaLoadingRef.current) return;
    galleryMetaLoadingRef.current = true;
    try {
      const metaModule = await import('../data/heroGalleryMeta.js');
      setGalleryMeta(metaModule.default);
    } catch (error) {
      console.error('Failed to load gallery metadata', error);
    } finally {
      galleryMetaLoadingRef.current = false;
    }
  };

  const [tooltipData, setTooltipData] = useState({
    visible: false,
    keyword: '',
    x: 0,
    y: 0,
    currentLang: 0
  });
  const [carouselDetail, setCarouselDetail] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(() => trialPlatforms[0]?.id || 'shutterstock');
  const [trialPreviewUrl, setTrialPreviewUrl] = useState('');
  const [trialFileName, setTrialFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState('');
  const scrollProgress = 0;
  const landingRef = useRef(null);
  const scrollProgressFillRef = useRef(null);
  const activeSectionIdRef = useRef('');
  const trialInputRef = useRef(null);
  const carouselBaseItems = galleryData.slice(0, 10);
  const homeBandLabels = useMemo(
    () => HOME_BAND_LABELS[language] || HOME_BAND_LABELS.en,
    [language]
  );
  const quickNavSections = useMemo(() => {
    const sections = [
      { id: 'guest-hero', label: homeBandLabels.hero },
      { id: 'guest-workflow', label: homeBandLabels.workflow },
      { id: 'guest-tools', label: homeBandLabels.tools },
      { id: 'guest-outputs', label: homeBandLabels.outputs },
      { id: 'guest-close', label: homeBandLabels.close }
    ];
    return sections.filter((item) => Boolean(item.label));
  }, [homeBandLabels]);
  const localizedCoreRouteLinks = useMemo(() => (
    coreRouteLinks.map((route) => ({
      ...route,
      href: localizeSeoHref(route.href, language),
      label: homeContent.routeLabels?.[route.href] || route.label
    }))
  ), [homeContent, language]);
  const localizedStats = mergeLocalizedItems(stats, homeContent.stats);
  const localizedToolIntentLinks = useMemo(() => {
    return toolIntentLinks.map((item) => ({
      ...item,
      title: homeContent.routeLabels?.[item.href] || item.title,
      description: '',
      href: localizeSeoHref(item.href, language)
    }));
  }, [homeContent.routeLabels, language]);
  const localizedPublicToolIntentLinks = useMemo(() => {
    const localizedItems = Array.isArray(homeContent.toolIntentLinks) ? homeContent.toolIntentLinks : [];
    return toolIntentLinks.map((item) => {
      const title = homeContent.routeLabels?.[item.href] || item.title;
      const exactMatch = localizedItems.find((candidate) => candidate?.title === title);
      return {
        ...item,
        title,
        description: exactMatch?.description || homeContent.guidesDesc,
        pill: exactMatch?.pill || item.pill,
        href: localizeSeoHref(item.href, language)
      };
    });
  }, [homeContent.guidesDesc, homeContent.routeLabels, homeContent.toolIntentLinks, language]);
  const localizedComparisonIntentLinks = useMemo(() => {
    const localizedTitles = HOME_COMPARISON_TITLES[language] || {};

    return comparisonIntentLinks.map((item) => {
      if (item.href === '/alternatives') {
        return {
          ...item,
          title: homeContent.routeLabels?.[item.href] || item.title,
          description: '',
          href: localizeSeoHref(item.href, language)
        };
      }

      const slug = item.href.split('/').filter(Boolean).pop();

      return {
        ...item,
        title: localizedTitles[slug] || item.title,
        description: '',
        href: localizeSeoHref(item.href, language)
      };
    });
  }, [homeContent.routeLabels, language]);
  const localizedPublicComparisonIntentLinks = useMemo(() => (
    localizedComparisonIntentLinks.map((item) => ({
      ...item,
      description: homeContent.toolIntentLinks?.find((candidate) => candidate?.title === item.title)?.description
        || homeContent.sectionCompareDesc
    }))
  ), [homeContent.sectionCompareDesc, homeContent.toolIntentLinks, localizedComparisonIntentLinks]);
  const localizedFeatures = mergeLocalizedItems(features, homeContent.features);
  const localizedStudioModules = mergeLocalizedItems(studioModules, homeContent.studioModules)
    .filter((item) => !containsRetiredPublicByokCopy(item));
  const localizedStudioProof = homeContent.studioProof || studioProof.map((item) => item.label);
  const localizedStudioProofItems = useMemo(() => (
    localizedStudioProof.map((label, index) => ({
      label,
      icon: (studioProof[index] || studioProof[0]).icon
    }))
  ), [localizedStudioProof]);
  const localizedComparison = homeContent.comparisonLists || comparison;
  const localizedMarketHighlights = mergeLocalizedItems(marketIntelligenceHighlights, homeContent.marketIntelligenceHighlights);
  const localizedVisualIntelligence = mergeLocalizedItems(visualIntelligence, homeContent.visualIntelligence);
  const localizedImageGeneratorHighlights = mergeLocalizedItems(imageGeneratorHighlights, homeContent.imageGeneratorHighlights);
  const localizedSafeguardCards = mergeLocalizedItems(safeguardCards, homeContent.safeguardCards);
  const localizedSteps = mergeLocalizedItems(steps, homeContent.steps);
  const localizedPlatformList = homeContent.platformList || platformList;
  const localizedPlatformPresets = mergeLocalizedItems(platformPresets, homeContent.platformPresets);
  const localizedTestimonials = mergeLocalizedItems(testimonials, homeContent.testimonials);
  const localizedFaqs = mergeLocalizedItems(faqs, homeContent.faqs)
    .filter((item) => !containsRetiredPublicByokCopy(item));
  const localizedMetadataSamples = mergeLocalizedItems(metadataSamples, homeContent.metadataSamples);
  const localizedMetadataHighlights = metadataCapabilityContent.highlights || [];
  const localizedMetadataFlowSteps = metadataCapabilityContent.flowSteps || [];
  const localizedMetadataCapabilityCards = metadataCapabilityContent.cards || [];
  const localizedHelpHref = localizeSeoHref('/help', language);
  const localizedVideoDemoHref = localizeSeoHref('/video/stocktag-metadata-studio-demo', language);

  useEffect(() => {
    if (trialPlatforms.length === 0) return;
    if (trialPlatforms.some((platform) => platform.id === selectedPlatform)) return;
    setSelectedPlatform(trialPlatforms[0].id);
  }, [trialPlatforms, selectedPlatform]);

  useEffect(() => {
    setActiveSectionId((previous) => {
      const nextSectionId = quickNavSections.some((section) => section.id === previous)
        ? previous
        : (quickNavSections[0]?.id || '');
      activeSectionIdRef.current = nextSectionId;
      return nextSectionId;
    });
  }, [quickNavSections]);

  useEffect(() => {
    return () => {
      if (trialPreviewUrl) {
        URL.revokeObjectURL(trialPreviewUrl);
      }
    };
  }, [trialPreviewUrl]);

  const scrollToSection = useCallback((sectionId) => {
    const section = document.getElementById(sectionId);
    if (!section) return;
    section.scrollIntoView({
      behavior: getScrollBehavior(),
      block: 'start'
    });
  }, []);

  const renderActionElement = useCallback(({
    href = '',
    onClick,
    className,
    children
  }) => {
    if (href) {
      return (
        <a
          href={href}
          className={className}
          onClick={(event) => {
            if (!onClick) return;
            if (
              event.button !== 0
              || event.metaKey
              || event.ctrlKey
              || event.shiftKey
              || event.altKey
            ) return;
            event.preventDefault();
            onClick(event);
          }}
        >
          {children}
        </a>
      );
    }

    return (
      <button type="button" className={className} onClick={onClick}>
        {children}
      </button>
    );
  }, []);

  useEffect(() => {
    if (variant !== 'embedded') return undefined;
    if (quickNavSections.length === 0) return undefined;

    let animationFrame = null;

    const updateScrollState = () => {
      const documentElement = document.documentElement;
      const maxScroll = Math.max(0, documentElement.scrollHeight - window.innerHeight);
      const progress = maxScroll > 0 ? Math.min(1, window.scrollY / maxScroll) : 0;
      landingRef.current?.style.setProperty('--home-scroll-progress', String(progress));
      if (scrollProgressFillRef.current) {
        scrollProgressFillRef.current.style.transform = `scaleX(${progress})`;
      }

      const threshold = 170;
      let current = quickNavSections[0]?.id || '';

      for (let index = 0; index < quickNavSections.length; index += 1) {
        const section = quickNavSections[index];
        const element = document.getElementById(section.id);
        if (!element) continue;
        if (element.getBoundingClientRect().top <= threshold) {
          current = section.id;
        } else {
          break;
        }
      }

      if (activeSectionIdRef.current !== current) {
        activeSectionIdRef.current = current;
        setActiveSectionId(current);
      }
    };

    const onScrollOrResize = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        updateScrollState();
      });
    };

    updateScrollState();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [quickNavSections, variant]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const root = landingRef.current;
    if (!root) return undefined;

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReducedMotion || typeof window.IntersectionObserver !== 'function') {
      const revealAll = () => {
        root.querySelectorAll('[data-home-reveal]').forEach((node) => node.classList.add('is-visible'));
      };
      revealAll();
      const mutationObserver = typeof window.MutationObserver === 'function'
        ? new window.MutationObserver(revealAll)
        : null;
      mutationObserver?.observe(root, { childList: true, subtree: true });
      return () => mutationObserver?.disconnect();
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.01
      }
    );

    const observeRevealNodes = () => {
      root.querySelectorAll('[data-home-reveal]:not(.is-visible)').forEach((node) => {
        if (node.closest('.home-band--hero')) {
          node.classList.add('is-visible');
          return;
        }
        observer.observe(node);
      });
    };
    observeRevealNodes();

    const mutationObserver = typeof window.MutationObserver === 'function'
      ? new window.MutationObserver(observeRevealNodes)
      : null;
    mutationObserver?.observe(root, { childList: true, subtree: true });

    return () => {
      mutationObserver?.disconnect();
      observer.disconnect();
    };
  }, [blogPosts.length, language, quickNavSections.length, showGuestUi, showPricingPrompt]);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (!event.altKey || event.defaultPrevented) return;
      const activeElement = document.activeElement;
      const isTypingTarget = activeElement
        && (
          activeElement.tagName === 'INPUT'
          || activeElement.tagName === 'TEXTAREA'
          || activeElement.tagName === 'SELECT'
          || activeElement.isContentEditable
        );
      if (isTypingTarget) return;

      const pressedKey = event.key.toLowerCase();
      if (pressedKey === 's') {
        event.preventDefault();
        onScrollToStudio?.();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [onScrollToStudio]);

  const handleCarouselCardActivate = (event, item, fullItem, row) => {
    event.preventDefault();
    event.stopPropagation();
    void ensureGalleryMeta();

    const rect = event.currentTarget.getBoundingClientRect();
    setCarouselDetail((previous) => {
      if (previous?.item?.id === item.id) {
        return null;
      }
      return {
        item: fullItem,
        x: rect.left + rect.width / 2,
        y: rect.top - 12,
        row
      };
    });
  };

  const handleTrialFile = (file) => {
    if (!file || trialLoading) return;
    const isImage = String(file.type || '').startsWith('image/');
    if (!isImage) {
      if (onRunTrial) {
        onRunTrial(file, selectedPlatform);
      }
      return;
    }
    if (trialPreviewUrl) {
      URL.revokeObjectURL(trialPreviewUrl);
    }
    const nextPreview = URL.createObjectURL(file);
    setTrialPreviewUrl(nextPreview);
    setTrialFileName(file.name || '');
    if (onRunTrial) {
      onRunTrial(file, selectedPlatform);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    handleTrialFile(file);
  };

  const homeKeywords = toKeywordString(
    homeTitle,
    homeDescription,
    stats.map((item) => item.label),
    localizedToolIntentLinks.map((item) => item.title)
  );
  const homeAbout = buildThingEntities(
    homeTitle,
    homeDescription,
    stats.map((item) => item.label),
    localizedToolIntentLinks.map((item) => item.title)
  );
  const toolIntentSchema = buildItemListSchema({
    name: `${homeContent.sectionRoutesTitle} | Stocktag`,
    description: homeContent.sectionRoutesDesc || homeContent.guidesDesc,
    items: localizedToolIntentLinks,
    resolveName: (item) => item.title,
    resolveUrl: (item) => buildLocalizedUrl(SEO_BASE_URL, item.href, language)
  });
  const homeVideoSchema = buildVideoObjectSchema({
    video: STOCKTAG_METADATA_STUDIO_VIDEO,
    name: homeVideoCopy.title,
    description: homeVideoCopy.description,
    pageUrl: canonicalUrl,
    inLanguage: localeMarket.localeTag
  });

  const homepageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': ORGANIZATION_SCHEMA_ID,
      name: 'Stocktag',
      alternateName: 'Stocktag AI',
      url: SEO_BASE_URL,
      logo: {
        '@type': 'ImageObject',
        url: ORGANIZATION_LOGO,
        contentUrl: ORGANIZATION_LOGO,
        width: 512,
        height: 288
      },
      email: ORGANIZATION_SUPPORT_EMAIL,
      sameAs: ORGANIZATION_SAME_AS,
      areaServed: SUPPORTED_SEO_COUNTRIES,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: ORGANIZATION_SUPPORT_EMAIL,
        availableLanguage: SUPPORTED_SEO_LOCALE_TAGS
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': WEBSITE_SCHEMA_ID,
      name: 'Stocktag',
      alternateName: 'Stocktag AI',
      url: SEO_BASE_URL,
      inLanguage: localeMarket.localeTag,
      keywords: homeKeywords,
      hasPart: localizedToolIntentLinks.slice(0, 8).map((item) => ({
        '@type': 'WebPage',
        name: item.title,
        url: buildLocalizedUrl(SEO_BASE_URL, item.href, language)
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': buildWebPageSchemaId(canonicalUrl),
      name: homeTitle,
      description: homeDescription,
      url: canonicalUrl,
      inLanguage: localeMarket.localeTag,
      keywords: homeKeywords,
      about: homeAbout,
      primaryImageOfPage: HOME_IMAGE,
      isPartOf: {
        '@id': WEBSITE_SCHEMA_ID
      },
      publisher: { '@id': ORGANIZATION_SCHEMA_ID }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': SOFTWARE_APPLICATION_SCHEMA_ID,
      name: 'Stocktag Metadata Studio',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: canonicalUrl,
      description: homeDescription,
      inLanguage: localeMarket.localeTag,
      countriesSupported: SUPPORTED_SEO_COUNTRIES,
      availableLanguage: SUPPORTED_SEO_LOCALE_TAGS,
      applicationSubCategory: 'Stock metadata generator and keyword workflow',
      featureList: [
        ...stats.map((item) => item.label),
        ...localizedToolIntentLinks.slice(0, 4).map((item) => item.title)
      ],
      screenshot: HOME_IMAGE,
      isAccessibleForFree: true,
      offers: {
        '@type': 'Offer',
        price: 0,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: `${SEO_BASE_URL}/pricing`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: localizedFaqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    },
    homeVideoSchema,
    toolIntentSchema
  ];

  const LandingSections = variant === 'public'
    ? PremiumPublicHomeLanding
    : EmbeddedHomeLandingSections;

  return (
    <section
      ref={landingRef}
      className={`guest-landing ${variant === 'public' ? 'public-home-premium' : 'guest-landing-redesign'}`}
      data-guest-allow="true"
      data-home-variant={variant}
      style={{ '--home-scroll-progress': String(scrollProgress) }}
    >
      <PageDiscoveryLinks markdownPath="/ai/markdown/home.md" language={language} />
      <Helmet>
        <title>{homeTitle}</title>
        <meta name="description" content={homeDescription} />
        <meta name="author" content="Stocktag Team" />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
        <link rel="canonical" href={canonicalUrl} />
        {hreflangLinks.map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}

        <meta property="og:title" content={homeTitle} />
        <meta property="og:description" content={homeDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Stocktag" />
        <meta property="og:locale" content={ogLocale} />
        {ogLocaleAlternates.map((locale) => (
          <meta key={locale} property="og:locale:alternate" content={locale} />
        ))}
        <meta property="og:image" content={HOME_IMAGE} />
        <meta property="og:image:type" content={DEFAULT_SOCIAL_IMAGE.type} />
        <meta property="og:image:width" content={String(DEFAULT_SOCIAL_IMAGE.width)} />
        <meta property="og:image:height" content={String(DEFAULT_SOCIAL_IMAGE.height)} />
        <meta property="og:image:alt" content={DEFAULT_SOCIAL_IMAGE.alt} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stocktagai" />
        <meta name="twitter:title" content={homeTitle} />
        <meta name="twitter:description" content={homeDescription} />
        <meta name="twitter:image" content={HOME_IMAGE} />
        <meta name="twitter:image:alt" content={DEFAULT_SOCIAL_IMAGE.alt} />
      </Helmet>
      <SchemaOrg data={homepageSchema} />
      <LandingSectionsBoundary isPublic={variant === 'public'}>
      <LandingSections
        language={language}
        theme={theme}
        themeReady={themeReady}
        uiCopy={uiCopy}
        publicHomeUiCopy={publicHomeUiCopy}
        metadataStoryCopy={metadataStoryCopy}
        homeShowcaseCopy={homeShowcaseCopy}
        quickNavSections={quickNavSections}
        activeSectionId={activeSectionId}
        scrollProgressFillRef={scrollProgressFillRef}
        scrollToSection={scrollToSection}
        renderActionElement={renderActionElement}
        homeContent={homeContent}
        metadataCapabilityContent={metadataCapabilityContent}
        homeVideoCopy={homeVideoCopy}
        studioVideo={STOCKTAG_METADATA_STUDIO_VIDEO}
        salesCopy={salesCopy}
        showAuthenticatedUi={showAuthenticatedUi}
        showGuestUi={showGuestUi}
        showPricingPrompt={showPricingPrompt}
        isFreeMember={isFreeMember}
        isUnlimitedPlan={isUnlimitedPlan}
        studioHref={studioHref}
        demoHref={demoHref}
        tourHref={tourHref}
        loginHref={loginHref}
        pricingHref={pricingHref}
        demoStarted={demoStarted}
        onScrollToStudio={onScrollToStudio}
        onStartDemo={onStartDemo}
        onOpenTour={onOpenTour}
        onOpenLogin={onOpenLogin}
        onOpenPricing={onOpenPricing}
        localizedStats={localizedStats}
        localizedMetadataHighlights={localizedMetadataHighlights}
        localizedMetadataFlowSteps={localizedMetadataFlowSteps}
        localizedMetadataCapabilityCards={localizedMetadataCapabilityCards}
        metadataFlowIcons={metadataFlowIcons}
        metadataCapabilityCardIcons={metadataCapabilityCardIcons}
        localizedFeatures={localizedFeatures}
        localizedStudioProofItems={localizedStudioProofItems}
        localizedSteps={localizedSteps}
        localizedToolIntentLinks={variant === 'public' ? localizedPublicToolIntentLinks : localizedToolIntentLinks}
        localizedComparisonIntentLinks={variant === 'public' ? localizedPublicComparisonIntentLinks : localizedComparisonIntentLinks}
        localizedStudioModules={localizedStudioModules}
        localizedPlatformList={localizedPlatformList}
        localizedPlatformPresets={localizedPlatformPresets}
        localizedMarketHighlights={localizedMarketHighlights}
        localizedVisualIntelligence={localizedVisualIntelligence}
        localizedImageGeneratorHighlights={localizedImageGeneratorHighlights}
        localizedMetadataSamples={localizedMetadataSamples}
        localizedComparison={localizedComparison}
        localizedSafeguardCards={localizedSafeguardCards}
        localizedTestimonials={localizedTestimonials}
        localizedFaqs={localizedFaqs}
        localizedCoreRouteLinks={localizedCoreRouteLinks}
        trialFlowCopy={trialFlowCopy}
        selectedPlatform={selectedPlatform}
        setSelectedPlatform={setSelectedPlatform}
        trialPlatforms={trialPlatforms}
        trialLoading={trialLoading}
        trialError={trialError}
        trialPreviewUrl={trialPreviewUrl}
        trialFileName={trialFileName}
        trialImageAlt={trialImageAlt}
        dragActive={dragActive}
        setDragActive={setDragActive}
        handleDrop={handleDrop}
        trialInputRef={trialInputRef}
        handleTrialFile={handleTrialFile}
        carouselBaseItems={carouselBaseItems}
        isMounted={isMounted}
        galleryMeta={galleryMeta}
        ensureGalleryMeta={ensureGalleryMeta}
        hoveredRow={hoveredRow}
        setHoveredRow={setHoveredRow}
        handleCarouselCardActivate={handleCarouselCardActivate}
        carouselDetail={carouselDetail}
        setCarouselDetail={setCarouselDetail}
        setTooltipData={setTooltipData}
        translationLanguages={languages}
        localizedHelpHref={localizedHelpHref}
        localizedVideoDemoHref={localizedVideoDemoHref}
        blogPosts={blogPosts}
        blogPreviewRef={blogPreviewRef}
        onOpenBlog={onOpenBlog}
        onOpenBlogIndex={onOpenBlogIndex}
        isGuest={isGuest}
      />
      </LandingSectionsBoundary>
      {variant === 'embedded' && tooltipData.visible && (
        <div
          className="guest-translation-tooltip"
          style={{ left: tooltipData.x, top: tooltipData.y }}
          aria-hidden="true"
        >
          <div className="guest-translation-lang">
            {homeContent.translationHoverLabel}: {languages[tooltipData.currentLang]}
          </div>
          <div className="guest-translation-text">
            {keywordTranslations[tooltipData.keyword]?.[tooltipData.currentLang] || tooltipData.keyword}
          </div>
        </div>
      )}
    </section>
  );
};

const PUBLIC_HOME_PAYLOAD_KEY = '__publicHomePayload';

const isPayloadForLanguage = (payload, language) => (
  Boolean(payload)
  && payload.language === normalizeLanguage(language)
  && Boolean(payload.homeContent)
  && Boolean(payload.metadataCapabilityContent)
  && Boolean(payload.homeVideoCopy)
  && Boolean(payload.metadataStoryCopy)
  && Boolean(payload.publicHomeUiCopy)
  && Boolean(payload.homeShowcaseCopy)
);

const GuestLanding = (props) => {
  const appContext = usePublicAppContext();
  const language = appContext?.language || 'en';
  const serializedPayload = appContext?.texts?.[PUBLIC_HOME_PAYLOAD_KEY] || null;
  const [loadedPayload, setLoadedPayload] = useState(() => (
    isPayloadForLanguage(serializedPayload, language) ? serializedPayload : null
  ));

  useEffect(() => {
    if (isPayloadForLanguage(serializedPayload, language)) {
      setLoadedPayload(serializedPayload);
      return undefined;
    }

    if (isPayloadForLanguage(loadedPayload, language)) return undefined;

    let cancelled = false;
    import('../data/publicHomePayload.js')
      .then(({ getPublicHomePayload }) => {
        if (!cancelled) setLoadedPayload(getPublicHomePayload(language));
      })
      .catch(() => {
        // The existing route-level chunk recovery handler owns retry behavior.
      });

    return () => {
      cancelled = true;
    };
  }, [language, loadedPayload, serializedPayload]);

  const publicHomePayload = isPayloadForLanguage(serializedPayload, language)
    ? serializedPayload
    : (isPayloadForLanguage(loadedPayload, language) ? loadedPayload : null);

  if (!publicHomePayload) {
    return (
      <section
        className={`guest-landing ${props.variant === 'public' ? 'public-home-premium' : 'guest-landing-redesign'}`}
        data-home-variant={props.variant || 'embedded'}
        aria-busy="true"
      >
        <div className="guest-landing-loading" aria-hidden="true" />
      </section>
    );
  }

  return <GuestLandingContent {...props} publicHomePayload={publicHomePayload} />;
};

export default GuestLanding;
